/**
 * 화면의 날짜·시각은 모두 앱 시간대(서버 APP_TIMEZONE — /api/v1/me appTimezone)로 그리고 만든다(계획 문서 5.3 3d 결정 3).
 * 서버 규칙(휴가 근무일·예약 이용 시간·'오늘')이 그 시간대라서 — 브라우저가 다른 시간대여도(해외 출장) 같은 날·같은 시각을 본다.
 * 이 파일 밖에서는 브라우저 시간대 Date 메서드(getHours·toLocaleString·new Date(y, m, d) 등)를 쓰지 않는다(scripts/check-time-usage.mjs가 막는다).
 * 시각 값은 ISO 문자열이나 epoch ms로 주고받는다. 달력 날짜는 'YYYY-MM-DD'(시간대 없는 날 — 계산은 UTC 산술).
 */

let TZ = 'Asia/Seoul';
const cache = new Map();
/** 앱 시간대 설정 — loadAll이 /me를 받은 뒤 한 번 부른다 */
export function setAppTimezone(tz) {
  if (tz && tz !== TZ) { TZ = tz; cache.clear(); }
}
export const appTimezone = () => TZ;

const fmt = () => {
  let f = cache.get(TZ);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone: TZ, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', weekday: 'short' });
    cache.set(TZ, f);
  }
  return f;
};
const DOW = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const pad = (n) => String(n).padStart(2, '0');
const ms = (v) => (v instanceof Date ? v.getTime() : typeof v === 'number' ? v : Date.parse(v));

/** 앱 시간대 벽시계 부분 — { y, m(1~12), d, hh, mm, ss, dow(0=일) } */
export function parts(v = Date.now()) {
  const p = Object.fromEntries(fmt().formatToParts(new Date(ms(v))).map((x) => [x.type, x.value]));
  return { y: +p.year, m: +p.month, d: +p.day, hh: +p.hour, mm: +p.minute, ss: +p.second, dow: DOW[p.weekday] };
}

/** 'YYYY-MM-DD'(앱 시간대의 날) */
export const ymd = (v = Date.now()) => { const p = parts(v); return `${p.y}-${pad(p.m)}-${pad(p.d)}`; };
/** 오늘(앱 시간대) */
export const todayYmd = () => ymd(Date.now());
/** 'HH:MM' */
export const hm = (v) => { const p = parts(v); return `${pad(p.hh)}:${pad(p.mm)}`; };
/** 시각을 시간 소수로(10:30 → 10.5) */
export const hourOf = (v) => { const p = parts(v); return p.hh + p.mm / 60; };
/** 'YYYY-MM-DD HH:MM' */
export const dateTime = (v) => (v ? `${ymd(v)} ${hm(v)}` : '');
/** 'MM/DD' */
export const md = (v) => { const p = parts(v); return `${pad(p.m)}/${pad(p.d)}`; };

/** 그 시각의 앱 시간대와 UTC 차이(ms) */
function offsetAt(t) {
  const p = parts(t);
  return Date.UTC(p.y, p.m - 1, p.d, p.hh, p.mm, p.ss) - Math.floor(t / 1000) * 1000;
}
/**
 * 앱 시간대 벽시계 'YYYY-MM-DD' + 시(소수 — 10.5 = 10:30, 24 = 다음 날 0시)를 ISO로. 그날의 시간대 차이로 바꾸고 한 번 더 맞춘다(서머타임 경계)
 */
export function zonedIso(dateYmd, hours = 0) {
  const [y, m, d] = dateYmd.split('-').map(Number);
  const guess = Date.UTC(y, m - 1, d, 0, Math.round(hours * 60));
  let t = guess - offsetAt(guess);
  t = guess - offsetAt(t);
  return new Date(t).toISOString();
}

// ── 달력 날짜 산술(시간대 없는 날 — UTC로 센다) ──
const dayMs = (s) => { const [y, m, d] = s.split('-').map(Number); return Date.UTC(y, m - 1, d); };
const fromMs = (t) => { const x = new Date(t); return `${x.getUTCFullYear()}-${pad(x.getUTCMonth() + 1)}-${pad(x.getUTCDate())}`; };
export const addDays = (s, n) => fromMs(dayMs(s) + n * 86_400_000);
export const daysBetween = (a, b) => Math.round((dayMs(b) - dayMs(a)) / 86_400_000);
/** 요일(0=일) */
export const dowOfYmd = (s) => new Date(dayMs(s)).getUTCDay();
/** 그달의 날 수 */
export const daysInMonth = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();
/** 'YYYY-MM' 달을 k달 옮기기 */
export const shiftMonth = (y, m, k) => { const t = new Date(Date.UTC(y, m - 1 + k, 1)); return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1 }; };
/** 'YYYY-MM-DD'를 { y, m, d }로 */
export const splitYmd = (s) => { const [y, m, d] = s.split('-').map(Number); return { y, m, d }; };

/** 한국어 표시 — 'M월 D일(요일) HH:MM' 같은 표시는 Intl을 앱 시간대로(브라우저 시간대가 아니다) */
export const format = (v, opts) => new Intl.DateTimeFormat('ko-KR', { timeZone: TZ, ...opts }).format(new Date(ms(v)));
